import { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE = "sdf_cart_v2";

const keyFor = (id, variant) => `${id}__${variant || ""}`;

/**
 * Resolve the best displayable image for a product + variant combo.
 *
 * Priority (per iter34 cart-image bug fix):
 *   1. The selected variant's own image_url
 *   2. The product's main image_url
 *   3. ANY variant on the product that has an image_url  ← catches the
 *      common case where admin uploaded only variant images and left the
 *      main empty
 *   4. Empty string ("") — Cart/Checkout render fall back to the placeholder
 */
const resolveItemImage = (product, variant) => {
  if (variant?.image_url) return variant.image_url;
  if (product?.image_url) return product.image_url;
  const fallback = (product?.variants || []).find((v) => v?.image_url)?.image_url;
  return fallback || "";
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE)) || []; } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1, variant = null) => {
    setItems((prev) => {
      let defaultVariant = null;
      if (product.variants?.length) {
        const sorted = [...product.variants].sort((a, b) => a.price - b.price);
        defaultVariant = sorted[0];
      }
      const chosenVariant = variant || defaultVariant;
      const variantLabel = chosenVariant?.label || "";
      const variantPrice = chosenVariant?.price ?? product.price;
      const k = keyFor(product.product_id, variantLabel);
      const ex = prev.find((p) => keyFor(p.product_id, p.variant_label) === k);
      if (ex) return prev.map((p) => keyFor(p.product_id, p.variant_label) === k ? { ...p, quantity: p.quantity + qty } : p);
      return [...prev, {
        product_id: product.product_id, name: product.name, price: variantPrice,
        // Use variant image if uploaded, else main image, else any other
        // variant image as a safety net.  Admin-only-variant-image catalogs
        // were producing empty image_url in the cart before this fix.
        image_url: resolveItemImage(product, chosenVariant),
        quantity: qty,
        variant_label: variantLabel,
      }];
    });
  };

  const updateQty = (id, variant, qty) => {
    if (qty <= 0) return remove(id, variant);
    const k = keyFor(id, variant);
    setItems((prev) => prev.map((p) => keyFor(p.product_id, p.variant_label) === k ? { ...p, quantity: qty } : p));
  };

  const remove = (id, variant) => {
    const k = keyFor(id, variant);
    setItems((prev) => prev.filter((p) => keyFor(p.product_id, p.variant_label) !== k));
  };
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, count };
  }, [items]);

  // Memoize the provider value so consumers don't re-render on every parent
  // render. Methods (add/updateQty/remove/clear) are stable across renders
  // because they only close over `setItems` (which React guarantees is stable).
  const value = useMemo(
    () => ({ items, add, updateQty, remove, clear, ...totals }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, totals]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
